// The palette a member mixes a device's colour on: a field of saturation
// across and brightness down, a hue slider under it and the colour's code.
// Dragging previews on the device at once; letting go keeps the colour.

import { parseColor } from "../core/catalog.ts";
import { h } from "./dom.ts";
import { hexToHsv, hsvToHex, type Hsv } from "./color.ts";
import { t } from "./lang.ts";

export interface PaletteOptions {
  /** The colour to start from, `#rrggbb`. */
  color: string;
  /** What the palette is for, for its labels: the device's name. */
  subject: string;
  /** Called while the colour moves. */
  onPreview(color: string): void;
  /** Called when the colour is let go of. */
  onCommit(color: string): void;
}

const clamp = (value: number) => Math.max(0, Math.min(1, value));

export function createPalette(options: PaletteOptions): HTMLElement {
  const hsv: Hsv = hexToHsv(options.color);
  let committed = options.color;

  const knob = h("span", { class: "palette-knob" });
  const field = h("div", {
    class: "palette-field", role: "group", tabindex: "0",
    "aria-label": t("palette.field", { device: options.subject }),
  }, knob);
  const hue = h("input", { class: "palette-hue", type: "range", min: "0", max: "359", step: "1", "aria-label": t("palette.hue", { device: options.subject }) });
  const code = h("input", {
    class: "palette-code", type: "text", maxlength: "7", spellcheck: "false", autocapitalize: "off", autocomplete: "off",
    "aria-label": t("palette.code", { device: options.subject }),
  });
  const chip = h("span", { class: "palette-chip", "aria-hidden": "true" });

  const paint = (): string => {
    const color = hsvToHex(hsv);
    field.style.setProperty("--hue", `hsl(${hsv.h} 100% 50%)`);
    knob.style.left = `${hsv.s * 100}%`;
    knob.style.top = `${(1 - hsv.v) * 100}%`;
    knob.style.background = color;
    chip.style.background = color;
    hue.value = String(Math.round(hsv.h));
    if (document.activeElement !== code) code.value = color;
    return color;
  };
  const preview = () => options.onPreview(paint());
  const commit = () => {
    const color = paint();
    if (color === committed) return;
    committed = color;
    options.onCommit(color);
  };

  // The field: a press or a drag sets saturation and brightness from where the pointer is.
  const at = (event: PointerEvent) => {
    const box = field.getBoundingClientRect();
    hsv.s = clamp((event.clientX - box.left) / box.width);
    hsv.v = 1 - clamp((event.clientY - box.top) / box.height);
    preview();
  };
  let held = -1;
  field.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    held = event.pointerId;
    field.setPointerCapture(held);
    at(event);
    event.preventDefault();
    field.focus();
  });
  field.addEventListener("pointermove", (event) => {
    if (event.pointerId === held) at(event);
  });
  const release = (event: PointerEvent) => {
    if (event.pointerId !== held) return;
    held = -1;
    commit();
  };
  field.addEventListener("pointerup", release);
  field.addEventListener("pointercancel", release);
  field.addEventListener("keydown", (event) => {
    const step = event.shiftKey ? 0.1 : 0.02;
    if (event.key === "ArrowLeft") hsv.s = clamp(hsv.s - step);
    else if (event.key === "ArrowRight") hsv.s = clamp(hsv.s + step);
    else if (event.key === "ArrowUp") hsv.v = clamp(hsv.v + step);
    else if (event.key === "ArrowDown") hsv.v = clamp(hsv.v - step);
    else return;
    event.preventDefault();
    event.stopPropagation();
    preview();
  });
  field.addEventListener("keyup", (event) => {
    if (event.key.startsWith("Arrow")) commit();
  });

  hue.addEventListener("input", () => {
    hsv.h = Number(hue.value);
    // A grey has no hue to show: give it some colour so the slider does something.
    if (hsv.s < 0.08) hsv.s = 0.5;
    preview();
  });
  hue.addEventListener("change", commit);

  const typed = () => {
    const text = code.value.trim();
    const color = parseColor(text.startsWith("#") ? text : `#${text}`);
    if (!color) {
      code.value = hsvToHex(hsv);
      return;
    }
    Object.assign(hsv, hexToHsv(color));
    code.blur();
    preview();
    commit();
  };
  code.addEventListener("change", typed);
  // Keys typed here are text, not the room's or a device's controls.
  for (const type of ["keydown", "keyup"] as const) code.addEventListener(type, (event) => event.stopPropagation());

  paint();
  return h("div", { class: "palette" }, field, hue, h("label", { class: "palette-row" }, chip, code));
}
